import React from 'react';

/**
 * ScoreGauge — clean semi-circular gauge with segmented color zones.
 * Shows value position on a colored arc with clear zone labels.
 */
function ScoreGauge({
  value = 0,
  maxValue = 900,
  minValue = 300,
  label = '',
  sublabel = '',
  rating = '',
  ratingColor = '#667085',
  size = 140,
  invertColors = false
}) {
  const cx = size / 2;
  const cy = size / 2 + 6;
  const radius = size / 2 - 12;
  const strokeWidth = 10;

  const clampedValue = Math.max(minValue, Math.min(maxValue, value));
  const ratio = (clampedValue - minValue) / (maxValue - minValue);

  // Arc goes from π (left) to 0 (right)
  const needleAngle = Math.PI - ratio * Math.PI;
  const needleLen = radius - 6;
  const nx = cx + needleLen * Math.cos(needleAngle);
  const ny = cy - needleLen * Math.sin(needleAngle);

  // Color zones: 4 segments
  const zones = invertColors
    ? ['#16A34A', '#86EFAC', '#FDE68A', '#EF4444'] // green→red (low=good)
    : ['#EF4444', '#FDE68A', '#86EFAC', '#16A34A']; // red→green (high=good)

  const segmentAngle = Math.PI / zones.length;

  // Create arc segment paths
  const segmentPath = (startAng, endAng) => {
    const x1 = cx + radius * Math.cos(startAng);
    const y1 = cy - radius * Math.sin(startAng);
    const x2 = cx + radius * Math.cos(endAng);
    const y2 = cy - radius * Math.sin(endAng);
    return `M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`;
  };

  return (
    <div className="tu-score-gauge" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0.15rem', minWidth: 0 }}>
      <svg
        width="100%"
        height="auto"
        viewBox={`0 0 ${size} ${size / 2 + 20}`}
        style={{ maxWidth: `${size}px`, overflow: 'visible' }}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Background track */}
        <path
          d={segmentPath(Math.PI, 0)}
          fill="none"
          stroke="var(--border-color, #F1F5F9)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        {/* Color segments */}
        {zones.map((color, i) => {
          const sa = Math.PI - i * segmentAngle;
          const ea = Math.PI - (i + 1) * segmentAngle;
          return (
            <path
              key={i}
              d={segmentPath(sa, ea)}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap={i === 0 ? 'round' : i === zones.length - 1 ? 'round' : 'butt'}
              opacity={0.85}
            />
          );
        })}

        {/* Needle */}
        <line
          x1={cx} y1={cy}
          x2={nx} y2={ny}
          stroke="var(--text-color, #1E293B)"
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r={4} fill="var(--text-color, #1E293B)" />

        {/* Min/max labels */}
        <text x={cx - radius - 2} y={cy + 14} fontSize="8" fill="#94A3B8" textAnchor="start">
          {minValue}{maxValue === 100 ? '%' : ''}
        </text>
        <text x={cx + radius + 2} y={cy + 14} fontSize="8" fill="#94A3B8" textAnchor="end">
          {maxValue}{maxValue === 100 ? '%' : ''}
        </text>
      </svg>

      {/* Value + rating */}
      <span style={{ fontSize: '1.5rem', fontWeight: 700, color: ratingColor, lineHeight: 1, marginTop: '0' }}>
        {value}{maxValue === 100 ? '%' : ''}
      </span>
      {rating && (
        <span style={{ fontSize: '0.65rem', fontWeight: 600, color: ratingColor, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.1rem' }}>
          {rating}
        </span>
      )}
      {label && (
        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-color, #1E293B)', marginTop: '0.2rem', textAlign: 'center' }}>
          {label}
        </span>
      )}
      {sublabel && (
        <span style={{ fontSize: '0.62rem', color: '#94A3B8', marginTop: '0.02rem', textAlign: 'center' }}>
          {sublabel}
        </span>
      )}
    </div>
  );
}

export default ScoreGauge;
